import { useEffect } from 'react';

import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';

import {
  PC_LABEL,
  PC_LAYER,
  PC_POLY_BORDER,
  PC_POLY_FILL,
  PC_POLY_FOCUS_BORDER,
  PC_POLY_HOVER,
  PC_POLY_LABEL,
  PC_POLY_SOURCE,
  PC_SOURCE,
  REGION_LABEL,
} from '../CountryView/constants';
import { PostcodeMarker } from '../CountryView/types';
import { C, ThresholdConfig, getColor } from '../MapSharedData';

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

interface UseUnifiedPostcodeLayersOptions {
  mapRef: React.MutableRefObject<MapLibreMap | null>;
  ready: boolean;
  /** Whether the user has drilled into a country — gates visibility of the
   *  dots layer (world view keeps everything hidden). */
  activeCountry: string | null;
  /** All postcode markers for the active country (post-aggregation). */
  allMarkers: PostcodeMarker[];
  /** Selected region — dots are filtered to this region when set. */
  activeRegion: string | null;
  /** Threshold config for severity colors. */
  cfg: ThresholdConfig;
  /** Postcode polygons loaded for the active region (null = dots mode). */
  polygons: GeoJSON.FeatureCollection | null;
  /** Raw property name in postcode geojson holding the postcode value. */
  postcodeProperty: string;
  /** Currently focused postcode — drives the focus-border filter. */
  focusedPostcode: string | null;
}

/**
 * Owns the postcode sources (dots + polygons) and seven layers on the
 * unified map. Step 2 added the empty layers hidden; step 5 wires data piping
 * (setData on markers/polygons) and the dots↔polygons visibility swap that
 * the legacy CountryView usePostcodeLayers handled per-mount.
 */
export function useUnifiedPostcodeLayers({
  mapRef,
  ready,
  activeCountry,
  allMarkers,
  activeRegion,
  cfg,
  polygons,
  postcodeProperty,
  focusedPostcode,
}: UseUnifiedPostcodeLayersOptions) {
  // ── 1. Add empty sources + hidden layers once ──────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (map.getSource(PC_SOURCE)) return;

    // ── Dots ──────────────────────────────────────────────────────────────
    map.addSource(PC_SOURCE, { type: 'geojson', data: EMPTY_FC, generateId: true });

    map.addLayer({
      id: PC_LAYER,
      type: 'circle',
      source: PC_SOURCE,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 4, 10, 10],
        'circle-color': ['get', 'color'],
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 1.5,
        'circle-opacity': 0.95,
      },
      layout: { visibility: 'none' },
    });

    map.addLayer({
      id: PC_LABEL,
      type: 'symbol',
      source: PC_SOURCE,
      minzoom: 6,
      layout: {
        'text-field': ['get', 'postcode'],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 6, 9, 10, 11],
        'text-offset': [0, 1.2],
        'text-anchor': 'top',
        'text-allow-overlap': false,
        visibility: 'none',
      },
      paint: {
        'text-color': C.textPrimary,
        'text-halo-color': 'rgba(255,255,255,0.95)',
        'text-halo-width': 1.4,
      },
    });

    // ── Polygons ──────────────────────────────────────────────────────────
    map.addSource(PC_POLY_SOURCE, { type: 'geojson', data: EMPTY_FC, generateId: true });

    map.addLayer({
      id: PC_POLY_FILL,
      type: 'fill',
      source: PC_POLY_SOURCE,
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.6,
        'fill-outline-color': 'rgba(255,255,255,0.4)',
      },
      layout: { visibility: 'none' },
    });

    map.addLayer({
      id: PC_POLY_BORDER,
      type: 'line',
      source: PC_POLY_SOURCE,
      paint: { 'line-color': 'rgba(255,255,255,0.6)', 'line-width': 0.8 },
      layout: { visibility: 'none' },
    });

    map.addLayer({
      id: PC_POLY_HOVER,
      type: 'fill',
      source: PC_POLY_SOURCE,
      paint: {
        'fill-color': C.primary,
        'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.3, 0],
      },
      layout: { visibility: 'none' },
    });

    map.addLayer({
      id: PC_POLY_FOCUS_BORDER,
      type: 'line',
      source: PC_POLY_SOURCE,
      filter: ['==', ['get', 'postcode'], ''],
      paint: { 'line-color': C.primary, 'line-width': 3.5, 'line-opacity': 1 },
      layout: { visibility: 'none' },
    });

    map.addLayer({
      id: PC_POLY_LABEL,
      type: 'symbol',
      source: PC_POLY_SOURCE,
      minzoom: 8,
      filter: ['==', ['get', 'hasData'], true],
      layout: {
        'text-field': ['get', 'postcode'],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 9, 10, 12, 12, 14, 14],
        'text-anchor': 'center',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'symbol-placement': 'point',
        'text-padding': 2,
        visibility: 'none',
      },
      paint: {
        'text-color': C.textPrimary,
        'text-halo-color': 'rgba(255,255,255,0.95)',
        'text-halo-width': 1.6,
        'text-halo-blur': 0.5,
        'text-opacity': ['interpolate', ['linear'], ['zoom'], 9, 0, 10, 1],
      },
    });
  }, [mapRef, ready]);

  // ── 2. Update DOTS data: filter by active region, recolor by count ─────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (!map.getSource(PC_SOURCE)) return;

    const visibleMarkers = activeRegion
      ? allMarkers.filter((m) => m.region === activeRegion)
      : allMarkers;

    const features: GeoJSON.Feature[] = visibleMarkers.map((m) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [m.lng, m.lat] },
      properties: {
        postcode: m.postcode,
        place: m.place,
        count: m.count,
        region: m.region,
        color: getColor(m.count, cfg),
      },
    }));

    (map.getSource(PC_SOURCE) as maplibregl.GeoJSONSource).setData({
      type: 'FeatureCollection',
      features,
    });
  }, [mapRef, ready, allMarkers, activeRegion, cfg]);

  // ── 3. POLYGONS data + dots↔polygons visibility swap ───────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (!map.getSource(PC_POLY_SOURCE)) return;

    const usePolygons = !!polygons && !!activeRegion;

    // World view — hide everything postcode-related; drill-out timer takes
    // care of the broader country layers separately.
    if (!activeCountry) {
      for (const id of [
        PC_LAYER,
        PC_LABEL,
        PC_POLY_FILL,
        PC_POLY_BORDER,
        PC_POLY_HOVER,
        PC_POLY_FOCUS_BORDER,
        PC_POLY_LABEL,
      ]) {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'none');
      }
      return;
    }

    if (!usePolygons) {
      // Country overview OR region opened without polygons — dots only at
      // full opacity, polygons fully hidden.
      for (const id of [
        PC_POLY_FILL,
        PC_POLY_BORDER,
        PC_POLY_HOVER,
        PC_POLY_FOCUS_BORDER,
        PC_POLY_LABEL,
      ]) {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'none');
      }
      if (map.getLayer(PC_LAYER)) {
        map.setLayoutProperty(PC_LAYER, 'visibility', 'visible');
        map.setPaintProperty(PC_LAYER, 'circle-opacity', 0.95);
        map.setPaintProperty(PC_LAYER, 'circle-stroke-opacity', 1);
      }
      if (map.getLayer(PC_LABEL)) {
        map.setLayoutProperty(PC_LABEL, 'visibility', 'visible');
        map.setPaintProperty(PC_LABEL, 'text-opacity', 1);
      }
      return;
    }

    // Polygon mode — annotate features with normalised postcode + count.
    // Normalisation lets Canadian FSA polygons ('T5Z') match full postcodes
    // ('T5Z3K8') without disturbing equal-length systems (US ZCTA, AU POA).
    const regionMarkers = allMarkers.filter((m) => m.region === activeRegion);
    const norm = (s: string) => s.trim().toUpperCase().replace(/[\s-]/g, '');
    const normMarkers = regionMarkers.map((m) => ({
      code: norm(m.postcode ?? ''),
      count: m.count,
    }));

    const countForPolygon = (polyCode: string): number => {
      const np = norm(polyCode);
      if (!np) return 0;
      let total = 0;
      for (const m of normMarkers) {
        if (m.code === np || m.code.startsWith(np)) total += m.count;
      }
      return total;
    };

    const features = polygons.features.map((f) => {
      const pc = String(f.properties?.[postcodeProperty] ?? '');
      const count = countForPolygon(pc);
      return {
        ...f,
        properties: {
          ...f.properties,
          postcode: pc,
          count,
          color: count > 0 ? getColor(count, cfg) : C.noData,
          hasData: count > 0,
        },
      };
    });

    (map.getSource(PC_POLY_SOURCE) as maplibregl.GeoJSONSource).setData({
      type: 'FeatureCollection',
      features,
    });

    // Show polygons; dots remain visible but fade out by zoom as polygons
    // become legible. Brief 9→9.6 overlap with polygon labels avoids gaps.
    for (const id of [
      PC_POLY_FILL,
      PC_POLY_BORDER,
      PC_POLY_HOVER,
      PC_POLY_FOCUS_BORDER,
      PC_POLY_LABEL,
    ]) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'visible');
    }
    if (map.getLayer(PC_LAYER)) {
      map.setLayoutProperty(PC_LAYER, 'visibility', 'visible');
      map.setPaintProperty(PC_LAYER, 'circle-opacity', [
        'interpolate',
        ['linear'],
        ['zoom'],
        8,
        0.95,
        10,
        0,
      ]);
      map.setPaintProperty(PC_LAYER, 'circle-stroke-opacity', [
        'interpolate',
        ['linear'],
        ['zoom'],
        8,
        1,
        10,
        0,
      ]);
    }
    if (map.getLayer(PC_LABEL)) {
      map.setLayoutProperty(PC_LABEL, 'visibility', 'visible');
      map.setPaintProperty(PC_LABEL, 'text-opacity', [
        'interpolate',
        ['linear'],
        ['zoom'],
        9,
        1,
        9.6,
        0,
      ]);
    }
  }, [mapRef, ready, activeCountry, polygons, activeRegion, allMarkers, cfg, postcodeProperty]);

  // ── 4. Focus border filter for the selected postcode polygon ───────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (!map.getLayer(PC_POLY_FOCUS_BORDER)) return;
    map.setFilter(PC_POLY_FOCUS_BORDER, ['==', ['get', 'postcode'], focusedPostcode ?? '']);
  }, [mapRef, ready, focusedPostcode]);

  // ── 5. Enforce z-order: postcode layers above region fill, dots on top ─
  // Region/postcode layers can be added by separate effects in any order
  // depending on which geojson resolves first. moveLayer() with no beforeId
  // moves a layer to the very top — idempotent and order-independent.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const topOrder = [
      PC_POLY_FILL,
      PC_POLY_HOVER,
      PC_POLY_BORDER,
      REGION_LABEL,
      PC_POLY_FOCUS_BORDER,
      PC_LAYER,
      PC_POLY_LABEL,
      PC_LABEL,
    ];
    for (const id of topOrder) {
      if (map.getLayer(id)) map.moveLayer(id);
    }
  }, [mapRef, ready, polygons, allMarkers, activeRegion]);
}
